import { useState } from 'react'
import { useSelector } from 'react-redux'
import styles from '../InternalExternalDetail.module.css'
import Icon from './Icon'
import SectionHeader from './SectionHeader'
import DocItem from './DocItem'
import Button from './Button'

const S3_BASE = process.env.REACT_APP_S3_URL

async function downloadSelectedDocuments(token, userId, documentIds) {
  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/empprofile/documents/download-selected`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ userId, documentIds }),
    }
  );

  if (!res.ok) {
    throw new Error("Download failed");
  }

  // The API returns JSON containing a pre-signed S3 url for the zip.
  const result = await res.json();
  const url = result?.data?.url;

  if (!url) {
    throw new Error("Download url not found");
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = "documents.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const CredentialingSection = () => {
  const skillListings = useSelector(
    (state) => state.oneUserskillListingReducer?.skillListings
  )

  const userId = skillListings?.user_Schema?._id
  const credentials = skillListings?.Credentials || []
  const token = useSelector((state) => state.authReducer?.user?.authToken)

  const [selectedIds, setSelectedIds] = useState([])
  const [downloading, setDownloading] = useState(false)

  const documents = credentials
    .filter((doc) => !doc.isDeleted)
    .map((doc) => {
      const s3Key = doc?.s3Key
      const url = s3Key ? `${S3_BASE}/${s3Key}` : null
      return {
        id: doc?._id,
        title: doc?.title || doc?.originalFileName || '—',
        checked: selectedIds.includes(doc?._id),
        actions: url ? ['preview', 'download'] : [],
        url,
      }
    })

  const toggleDocument = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]
    )
  }

  const handleDownloadPdf = async () => {
    if (!selectedIds.length || !userId) return
    setDownloading(true)
    try {
      await downloadSelectedDocuments(token, userId, selectedIds)
    } catch (error) {
      console.error('Failed to download selected documents', error)
      alert(`Download failed: ${error.message}`)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <section className={styles.card}>
      <SectionHeader
        title="Credentialing"
        action={
          <Button
            variant="linkBtn"
            icon="download"
            iconClassName={styles.linkIcon}
            onClick={handleDownloadPdf}
            disabled={!selectedIds.length || downloading}
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        }
      />

      <p className={styles.helperText}>
        Select documents, drag to reorder, and download a single, organized PDF.
      </p>

      <div className={styles.toolsRow}>
        <label className={styles.searchBox}>
          <Icon name="search" className={styles.searchIcon} />
          <input type="search" placeholder="Search documents" />
        </label>
      </div>

      <div className={styles.docGrid}>
        {documents.length > 0
          ? documents.map((doc, index) => (
              <DocItem
                key={doc.id || doc.url || `${doc.title}-${index}`}
                {...doc}
                onToggle={() => toggleDocument(doc.id)}
              />
            ))
          : <p className={styles.helperText}>No documents available.</p>
        }
      </div>
    </section>
  )
}

export default CredentialingSection
